import React from "react";
import { SavedTournament, StandingsRow } from "../../types.ts";

interface StandingsTableProps {
  standings: StandingsRow[];
  tournaments: SavedTournament[];
}

const rankBadgeClass = (rank: number): string => {
  if (rank === 1) return "bg-amber-400 text-amber-950";
  if (rank === 2) return "bg-slate-300 text-slate-800";
  if (rank === 3) return "bg-orange-300 text-orange-950";
  return "bg-slate-100 text-slate-500";
};

/**
 * Průběžné pořadí šampionátu.
 *
 * Účast je sekvenční veličina, takže měřítko je jednobarevné
 * (modrá na světlejší modré), ne duhové – a hodnota je vždy i vypsaná.
 */
const StandingsTable: React.FC<StandingsTableProps> = ({
  standings,
  tournaments,
}) => {
  if (standings.length === 0) {
    return (
      <p className="text-slate-500 py-8 text-center">
        Zatím tu nejsou žádné odehrané turnaje.
      </p>
    );
  }

  // Poslední turnaje zleva, ať se tabulka vejde na obrazovku
  const shownTournaments = tournaments.slice(0, 8);

  const headCell =
    "px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm tabular-nums">
        <thead>
          <tr className="border-b border-slate-200">
            <th className={`${headCell} text-center w-12`}>#</th>
            <th className={`${headCell} text-left`}>Hráč</th>
            <th className={`${headCell} text-center`}>Body</th>
            <th className={`${headCell} text-center hidden sm:table-cell`}>
              Výher
            </th>
            <th className={`${headCell} text-center hidden md:table-cell`}>
              ⌀ / turnaj
            </th>
            <th className={`${headCell} text-left w-40`}>Účast</th>
            {shownTournaments.map((tournament) => (
              <th
                key={tournament.id}
                className={`${headCell} text-center hidden lg:table-cell w-14`}
                title={`Turnaj ${tournament.date}`}
              >
                {String(tournament.date).slice(0, 5)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {standings.map((row, index) => {
            const rank = index + 1;
            return (
              <tr
                key={row.playerId}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2 text-center">
                  <span
                    className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${rankBadgeClass(
                      rank
                    )}`}
                  >
                    {rank}
                  </span>
                </td>
                <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                  {row.playerName}
                </td>
                <td className="px-3 py-2 text-center font-bold text-slate-900">
                  {row.placementPoints}
                </td>
                <td className="px-3 py-2 text-center text-slate-600 hidden sm:table-cell">
                  {row.wins > 0 ? row.wins : "–"}
                </td>
                <td className="px-3 py-2 text-center text-slate-600 hidden md:table-cell">
                  {row.pointsPerVisit > 0
                    ? row.pointsPerVisit.toFixed(2)
                    : "–"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-blue-100 overflow-hidden min-w-[3rem]">
                      <div
                        className="h-full rounded-full bg-blue-600"
                        style={{ width: `${row.attendance}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 w-10 text-right">
                      {row.attendance} %
                    </span>
                  </div>
                </td>
                {shownTournaments.map((tournament, tIndex) => {
                  const value = row.results[tIndex];
                  return (
                    <td
                      key={tournament.id}
                      className="px-3 py-2 text-center hidden lg:table-cell"
                    >
                      {value !== null && value !== undefined && !isNaN(value) ? (
                        <span className="text-slate-700">{value}</span>
                      ) : (
                        <span className="text-slate-300">–</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="mt-3 text-xs text-slate-400">
        Body se udělují za umístění – vítěz bere tolik bodů, kolik hráčů se
        turnaje zúčastnilo. Sloupec <strong>Body</strong> je jejich součet přes
        všechny turnaje.
      </p>
    </div>
  );
};

export default StandingsTable;
